import { Editor, EditorContent, useEditor } from "@tiptap/react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { typeSize } from "../../theme";
import { uploadErrorMessage, uploadFile } from "../../uploads";
import { buildExtensions } from "./extensions";
import { MergeField, Toolbar } from "./Toolbar";

export type { MergeField };

type RichTextEditorProps = {
  value: string;
  onChange: (val: string) => void;
  mergeFields?: MergeField[];
  /** Total height in px, toolbar included. Defaults to 500. */
  height?: number;
  /** Show the image button and upload pasted or dropped images. Defaults to true. */
  allowImages?: boolean;
  "aria-label"?: string;
};

const Frame = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${(p) => p.theme.colors.grey3};
  border-radius: 4px;
  background: white;
  overflow: hidden;

  &:focus-within {
    border-color: ${(p) => p.theme.colors.primary};
  }
`;

const Status = styled.div<{ error?: boolean }>`
  padding: 4px 10px;
  font-size: ${typeSize("small")};
  color: ${(p) => (p.error ? p.theme.colors.danger : p.theme.colors.body)};
  border-bottom: 1px solid ${(p) => p.theme.colors.grey3};
`;

/** The editing area, styled like stored HTML is when shown (RawContent). */
const Scroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  cursor: text;

  > div {
    min-height: 100%;
  }

  .ProseMirror {
    min-height: 100%;
    padding: 12px 14px;
    outline: none;
    font-family: ${(p) => p.theme.fonts.body};
    font-size: ${typeSize("body")};
    color: ${(p) => p.theme.colors.body};
    line-height: 1.5;
  }

  .ProseMirror > :first-child {
    margin-top: 0;
  }

  p {
    margin: 0 0 12px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: 600;
    margin: 16px 0 12px;
  }

  h1 {
    font-size: ${(p) => p.theme.fontSizes[6]}px;
  }

  h2 {
    font-size: ${(p) => p.theme.fontSizes[5]}px;
  }

  h3 {
    font-size: ${(p) => p.theme.fontSizes[4]}px;
  }

  ul,
  ol {
    margin: 0 0 12px;
    padding-left: 28px;
  }

  li > p {
    margin-bottom: 6px;
  }

  a {
    color: ${(p) => p.theme.colors.primary};
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  img.ProseMirror-selectednode {
    outline: 2px solid ${(p) => p.theme.colors.primary};
  }

  pre {
    padding: 8px 10px;
    border-radius: 4px;
    background: ${(p) => p.theme.colors.grey5};
    white-space: pre-wrap;
  }

  table {
    border-collapse: collapse;
    margin: 0 0 12px;
  }

  td,
  th {
    border: 1px solid ${(p) => p.theme.colors.grey3};
    padding: 4px 8px;
    vertical-align: top;
  }
`;

const imageFiles = (list?: FileList | null) =>
  Array.from(list ?? []).filter((f) => f.type.startsWith("image/"));

/**
 * The HTML to save. Empty documents are "", as before, so required-field
 * checks still work. Tiptap wraps list item text in <p>; plain wrappers are
 * removed so lists keep TinyMCE's spacing when shown or emailed.
 */
function toHtml(editor: Editor): string {
  if (editor.isEmpty) return "";
  const tpl = document.createElement("template");
  tpl.innerHTML = editor.getHTML();
  tpl.content.querySelectorAll("li").forEach((li) => {
    const first = li.firstElementChild;
    const paragraphs = Array.from(li.children).filter((c) => c.tagName === "P");
    if (
      first != null &&
      first.tagName === "P" &&
      paragraphs.length === 1 &&
      first.attributes.length === 0
    ) {
      first.replaceWith(...Array.from(first.childNodes));
    }
  });
  // Tiptap keeps an empty paragraph after a closing list or table so the
  // cursor can leave it; don't save it.
  let last = tpl.content.lastElementChild;
  while (last != null && last.tagName === "P" && last.innerHTML === "") {
    const prev = last.previousElementSibling;
    last.remove();
    last = prev;
  }
  return tpl.innerHTML;
}

export function RichTextEditor({
  value,
  onChange,
  mergeFields,
  height,
  allowImages = true,
  "aria-label": ariaLabel = "Text editor",
}: RichTextEditorProps) {
  const [status, setStatus] = useState<{ text: string; error?: boolean }>();
  const fileInput = useRef<HTMLInputElement>(null);

  // The editor is created once; these refs let its callbacks see new props.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastHtml = useRef(value);
  const uploadRef = useRef<(files: File[], pos?: number) => void>(() => {});
  const allowImagesRef = useRef(allowImages);
  allowImagesRef.current = allowImages;

  const extensions = useMemo(buildExtensions, []);

  const editor = useEditor({
    extensions,
    content: value || "",
    editorProps: {
      attributes: {
        spellcheck: "true",
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": ariaLabel,
      },
      handlePaste: (_view, event) => {
        const files = imageFiles(event.clipboardData?.files);
        if (files.length === 0 || !allowImagesRef.current) return false;
        uploadRef.current(files);
        return true;
      },
      handleDrop: (view, event, _slice, moved) => {
        if (moved || !allowImagesRef.current) return false;
        const files = imageFiles(event.dataTransfer?.files);
        if (files.length === 0) return false;
        const at = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        uploadRef.current(files, at?.pos);
        return true;
      },
    },
    onUpdate: ({ editor: e }) => {
      const html = toHtml(e);
      lastHtml.current = html;
      onChangeRef.current(html);
    },
  });

  // Load the value when the parent changes it (a reset after saving, data
  // arriving after the first render), but not when it is our own edit.
  useEffect(() => {
    if (editor == null || editor.isDestroyed) return;
    if (value !== lastHtml.current) {
      lastHtml.current = value;
      editor.commands.setContent(value || "", { emitUpdate: false });
    }
  }, [value, editor]);

  uploadRef.current = async (files, pos) => {
    if (editor == null) return;
    for (const file of files) {
      try {
        setStatus({ text: `Uploading ${file.name}…` });
        const key = await uploadFile(file, (pct) =>
          setStatus({ text: `Uploading ${file.name}… ${pct}%` }),
        );
        if (editor.isDestroyed) return;
        const image = {
          type: "image",
          attrs: { src: `${process.env.REACT_APP_STATIC_ROOT}/${key}`, alt: "" },
        };
        const chain = editor.chain().focus();
        (pos != null
          ? chain.insertContentAt(pos, image)
          : chain.insertContent(image)
        ).run();
        setStatus(undefined);
      } catch (err) {
        setStatus({ text: uploadErrorMessage(err), error: true });
        return;
      }
    }
  };

  return (
    <Frame style={{ height: height ?? 500 }}>
      {editor != null && (
        <Toolbar
          editor={editor}
          mergeFields={mergeFields}
          onPickImage={
            allowImages ? () => fileInput.current?.click() : undefined
          }
        />
      )}
      {status != null && (
        <Status error={status.error} role={status.error ? "alert" : "status"}>
          {status.text}
        </Status>
      )}
      <Scroll onClick={() => editor?.commands.focus()}>
        <EditorContent editor={editor} />
      </Scroll>
      {allowImages && (
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const files = imageFiles(e.target.files);
            e.target.value = "";
            if (files.length > 0) uploadRef.current(files);
          }}
        />
      )}
    </Frame>
  );
}
